import type { Language } from './types';

export interface PluralForms {
  one: string;   // 1 день / 1 day
  few: string;   // 2-4 дня (Russian only)
  many: string;  // 5+ дней / 2+ days
}

/**
 * Returns the correct plural form based on the number and language.
 *
 * Russian pluralization rules:
 * - one: 1, 21, 31... (but not 11, 111...)
 * - few: 2-4, 22-24... (but not 12-14, 112-114...)
 * - many: 0, 5-20, 25-30...
 *
 * English pluralization rules:
 * - one: 1
 * - many: 0, 2+
 *
 * Uzbek has no grammatical number, uses 'many' form for all.
 * Persian counts the same way: a noun after a numeral stays singular, so one form for all.
 */
export function pluralize(n: number, forms: PluralForms, language: Language): string {
  const absN = Math.abs(n);

  if (language === 'uz' || language === 'fa') {
    // Uzbek and Persian have no plural forms
    return forms.many;
  }

  if (language === 'en') {
    return absN === 1 ? forms.one : forms.many;
  }

  // Russian pluralization
  const mod10 = absN % 10;
  const mod100 = absN % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return forms.one;
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return forms.few;
  }

  return forms.many;
}
